// Keeps `aria-disabled` in sync with the native `disabled` attribute on
// every <button>, <input>, <select> and <textarea> in the document.
//
// Why: WCAG/ARIA recommends exposing `aria-disabled="true"` whenever a
// control is disabled, because some assistive technologies don't read the
// native `disabled` attribute consistently and won't announce why the
// control is unavailable. Tracked by Linear CIR-2816 (AF-007).
//
// We do this globally with a MutationObserver instead of refactoring 60+
// usage sites individually. The observer only listens for attribute changes
// on `disabled` / `aria-disabled`, so overhead is negligible.

const TARGET_TAGS = new Set(["BUTTON", "INPUT", "SELECT", "TEXTAREA"]);

function syncOne(el) {
  if (!el || !TARGET_TAGS.has(el.tagName)) return;
  const disabled = el.hasAttribute("disabled");
  const ariaDisabled = el.getAttribute("aria-disabled");
  if (disabled && ariaDisabled !== "true") {
    el.setAttribute("aria-disabled", "true");
  } else if (!disabled && ariaDisabled === "true") {
    el.removeAttribute("aria-disabled");
  }
}

function syncSubtree(root) {
  if (!root || root.nodeType !== 1) return;
  if (TARGET_TAGS.has(root.tagName)) syncOne(root);
  if (root.querySelectorAll) {
    root.querySelectorAll("button, input, select, textarea").forEach(syncOne);
  }
}

let observer = null;

export function useAriaDisabledSync() {
  if (observer) return; // already initialized
  if (typeof window === "undefined") return;

  // Initial pass over whatever is already in the DOM.
  syncSubtree(document.body);

  observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      if (m.type === "attributes" && m.target && m.target.nodeType === 1) {
        // disabled changed on a control → sync aria-disabled
        if (m.attributeName === "disabled") syncOne(m.target);
      } else if (m.type === "childList") {
        // new nodes added → sync them and their subtrees
        m.addedNodes.forEach(n => syncSubtree(n));
      }
    }
  });

  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["disabled"]
  });
}
